import { Box, Button, Divider, Radio, RadioGroup, Stack, Tooltip, Typography } from "@mui/material";
import { SelectableOption, UserAvatar } from "@zfp-akademie-a-s/zfp-design";
import { AddIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { useGetAnalysisClients } from "Infrastructure/ApiZfp/AnalyzerApi";
import { AnalysisClientItemAgeCategoryType, AnalysisClientItemRelationType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { isPlannedChild } from "Infrastructure/Utils/ClientUtils";
import { format } from "date-fns";
import { type FC, useContext, useEffect, useState } from "react";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { FamilyMemberDrawer } from "../Family/FamilyMemberDrawer";
import { AdditionalInformation } from "./AdditionalInformation";
import { ClientSolution } from "./ClientSolution";
import { RiskInsurance } from "./RiskInsurance";
import { StateAid } from "./StateAid";

type Props = {
  analysisId: string;
  versionId: string;
};

export const InsurancePane: FC<Props> = ({ analysisId, versionId }) => {
  const { readOnly, canAddFamilyMember } = useContext(AnalysisDetailContext);
  const { t } = useResource();

  const { data: clientRelationsData } = useGetAnalysisClients(analysisId, versionId);

  const defaultFamilyMember = clientRelationsData?.clients.find((client) => client.relationType === AnalysisClientItemRelationType.Client);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [familyMemberId, setFamilyMemberId] = useState(defaultFamilyMember?.guid);
  useEffect(() => {
    setFamilyMemberId(defaultFamilyMember?.guid);
  }, [defaultFamilyMember?.guid]);

  const currentFamilyMember = clientRelationsData?.clients.find((client) => client.guid === familyMemberId);

  return (
    <Box sx={{ marginBottom: "120px !important" }}>
      <Stack direction="row">
        <Stack spacing={Spacing.XXXL} pr={Spacing.XXXL} flex={4}>
          <Box display="flex" justifyContent="space-between">
            <Box>
              <Typography variant="h3">{t(Resources.AnalysisDetail.TabMenu.Insurance)}</Typography>
              <Typography variant="body2" color="secondary">
                {t(Resources.AnalysisDetail.Insurance.Subtitle)}
              </Typography>
            </Box>
            <Tooltip title={(!readOnly && !canAddFamilyMember ? t(Resources.AnalysisDetail.Family.AddMemberOwnerOnly) : undefined) ?? ""}>
              <Box component="span" display="inline-flex">
                <Button
                  variant="contained"
                  disabled={!canAddFamilyMember}
                  color="secondary"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setDrawerOpen(true);
                  }}
                >
                  {t(Resources.AnalysisDetail.Insurance.AddMember)}
                </Button>
              </Box>
            </Tooltip>
          </Box>
          {clientRelationsData && (
            <RadioGroup
              aria-label={t(Resources.AnalysesListing.Table.FamilyMembers.Title)}
              value={familyMemberId ?? null}
              onChange={(_, clientId) => setFamilyMemberId(clientId)}
              sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: Spacing.L }}
            >
              {clientRelationsData.clients
                .filter((client) => !isPlannedChild(client))
                .map((client) => {
                  // the member's avatar draws the Radio, as the type icons do in the goal drawer
                  const avatar = <UserAvatar size="small" firstName={client.firstName} lastName={client.surname} />;

                  return (
                    <SelectableOption
                      key={client.guid}
                      value={client.guid}
                      control={<Radio icon={avatar} checkedIcon={avatar} />}
                      label={
                        <Stack>
                          <Typography variant="body2" fontWeight={600}>
                            {client.firstName} {client.surname}
                          </Typography>
                          <Typography variant="caption" color="secondary">
                            {client.birthDate ? format(new Date(client.birthDate), "d. M. yyyy") : "-"}
                          </Typography>
                        </Stack>
                      }
                      sx={{ borderColor: "divider", py: 3 }}
                    />
                  );
                })}
            </RadioGroup>
          )}
          {currentFamilyMember?.ageCategoryType === AnalysisClientItemAgeCategoryType.adult && (
            <StateAid clientId={familyMemberId} analysisId={analysisId} versionId={versionId} />
          )}
          <Divider />
          {!!familyMemberId && (
            <RiskInsurance
              isAdult={currentFamilyMember?.ageCategoryType === AnalysisClientItemAgeCategoryType.adult}
              clientId={familyMemberId}
              analysisId={analysisId}
              versionId={versionId}
            />
          )}
        </Stack>
        {/* !important here because of parent spacing */}
        <Divider orientation="vertical" flexItem sx={{ margin: "-24px 0 0 0 !important" }} />
        <Stack spacing={Spacing.XL} mt={Spacing.S} px={Spacing.XXXL} sx={{ flex: 2 }}>
          {!!familyMemberId && <ClientSolution analysisId={analysisId} versionId={versionId} clientId={familyMemberId} />}
          <Divider />
          <AdditionalInformation analysisId={analysisId} versionId={versionId} clientId={familyMemberId} />
        </Stack>
      </Stack>

      <FamilyMemberDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </Box>
  );
};
